import {
  Box,
  Divider,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useDocumentVersionAutofillValuesQuery } from '@client/graphql/__generated__/document-operations';
import {
  DocumentVersionFieldFragment,
  InternalGetIngestionDocumentFragment,
} from '@client/graphql/__generated__/types';
import { Columns3Icon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { FieldValuesComparisonModal } from './FieldValuesComparisonModal';

interface ExtractedFieldsListProps {
  selectedDocument: InternalGetIngestionDocumentFragment;
  onFieldClick: (mappingKeys: string[], contractDocumentId?: string) => void;
}

interface ExtractedField {
  mappingKey: string;
  value: string | number | boolean | null | undefined;
  exists: boolean;
  isComputed?: boolean;
}

interface ContractDocumentFields {
  contractDocumentId: string;
  documentName: string;
  fields: ExtractedField[];
  isInteractive: boolean;
}

export const ExtractedFieldsList: FC<ExtractedFieldsListProps> = ({
  selectedDocument,
  onFieldClick,
}) => {
  const {
    isOpen: isComparisonModalOpen,
    onOpen: onComparisonModalOpen,
    onClose: onComparisonModalClose,
  } = useDisclosure();

  const [selectedContractDocId, setSelectedContractDocId] = useState<
    string | null
  >(null);

  const handleCompareClick = (contractDocumentId: string) => {
    setSelectedContractDocId(contractDocumentId);
    onComparisonModalOpen();
  };

  const selectedContractDocuments = useMemo(() => {
    if (!selectedContractDocId) return [];

    return (
      selectedDocument.contractDocuments?.filter(
        (cd) => cd.id === selectedContractDocId
      ) ?? []
    );
  }, [selectedContractDocId, selectedDocument.contractDocuments]);

  // Get document version IDs and merged field values for the autofill query
  const { documentVersionIds, allMergedFieldValues } = useMemo(() => {
    const docVersionIds: string[] = [];
    const mergedValues: Record<string, unknown> = {};

    selectedDocument.contractDocuments?.forEach((contractDoc) => {
      if (contractDoc.documentVersion?.id) {
        docVersionIds.push(contractDoc.documentVersion.id);
      }
      const mergedFieldValues = contractDoc.mergedFieldValues as
        | Record<string, unknown>
        | undefined;
      if (mergedFieldValues) {
        Object.assign(mergedValues, mergedFieldValues);
      }
    });

    return {
      documentVersionIds: docVersionIds,
      allMergedFieldValues: mergedValues,
    };
  }, [selectedDocument.contractDocuments]);

  // Fetch computed values (readOnlyAutofillValues) for the document versions
  const { data: autofillData } = useDocumentVersionAutofillValuesQuery({
    variables: {
      documentVersionIds,
      fieldValues: allMergedFieldValues as Record<
        string,
        string | number | boolean | string[] | undefined
      >,
      variableValues: {},
    },
    skip: documentVersionIds.length === 0,
  });

  const computedValues = autofillData?.documentVersionAutofillValues
    ?.readOnlyAutofillValues as Record<string, unknown> | undefined;

  // Group fields by contract document
  const fieldsByContractDoc = useMemo(() => {
    const groupedFields: ContractDocumentFields[] = [];

    if (!selectedDocument.contractDocuments) {
      return groupedFields;
    }

    // selectedDocument.fields is a map of contractDocId -> array of field objects with extracted values
    const fieldsByContractDocId = selectedDocument.fields as unknown as
      | Record<string, DocumentVersionFieldFragment[]>
      | undefined;

    // Iterate through each contract document
    selectedDocument.contractDocuments.forEach((contractDoc) => {
      const contractDocId = contractDoc.id;
      const documentName =
        contractDoc.documentVersion?.document?.name || 'Unnamed Document';

      // Get the merged field values from the contract document
      const mergedFieldValues = contractDoc.mergedFieldValues as
        | Record<string, unknown>
        | undefined;

      const extractedFieldsArray = fieldsByContractDocId?.[contractDocId];
      let extractedFields: ExtractedField[] = [];
      let isInteractive = false;

      // Track which mapping keys we've already added
      const addedMappingKeys = new Set<string>();

      if (extractedFieldsArray && extractedFieldsArray.length > 0) {
        // Map the extracted fields to our display format
        extractedFields = extractedFieldsArray.map((field) => {
          addedMappingKeys.add(field.mappingKey);
          const exists =
            mergedFieldValues !== null &&
            mergedFieldValues !== undefined &&
            field.mappingKey in mergedFieldValues;

          return {
            mappingKey: field.mappingKey,
            value: exists
              ? (mergedFieldValues[field.mappingKey] as
                  | string
                  | number
                  | boolean
                  | null
                  | undefined)
              : undefined,
            exists,
            isComputed: false,
          };
        });
        isInteractive = true;
      } else if (mergedFieldValues) {
        const mergedEntries = Object.entries(mergedFieldValues);
        if (mergedEntries.length === 0 && !computedValues) {
          return;
        }

        extractedFields = mergedEntries.map(([mappingKey, value]) => {
          addedMappingKeys.add(mappingKey);

          return {
            mappingKey,
            value: value as string | number | boolean | null | undefined,
            exists: true,
            isComputed: false,
          };
        });
      }

      // Add computed values that aren't already in the extracted fields
      if (computedValues) {
        Object.entries(computedValues).forEach(([mappingKey, value]) => {
          if (!addedMappingKeys.has(mappingKey)) {
            extractedFields.push({
              mappingKey,
              value: value as string | number | boolean | null | undefined,
              exists: true,
              isComputed: true,
            });
          }
        });
      }

      if (extractedFields.length > 0) {
        groupedFields.push({
          contractDocumentId: contractDocId,
          documentName,
          fields: extractedFields,
          isInteractive,
        });
      }
    });

    return groupedFields;
  }, [selectedDocument, computedValues]);

  // Helper to format field values
  const formatValue = (
    value: unknown,
    exists: boolean
  ): { message: string; theme: string } => {
    if (!exists) {
      return {
        message: '<missing>',
        theme: 'compliance.debug.code.text.warning',
      };
    }
    if (value === null) {
      return {
        message: '<null>',
        theme: 'compliance.debug.code.text.warning',
      };
    }
    if (value === undefined) {
      return {
        message: '<undefined>',
        theme: 'compliance.debug.code.text.warning',
      };
    }
    if (typeof value === 'string') {
      return { message: value, theme: 'compliance.debug.text' };
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return { message: String(value), theme: 'compliance.debug.text' };
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return {
          message: '<empty array>',
          theme: 'compliance.debug.code.text.warning',
        };
      }

      return { message: value.join(', '), theme: 'compliance.debug.text' };
    }
    if (typeof value === 'object') {
      return { message: JSON.stringify(value), theme: 'compliance.debug.text' };
    }

    return { message: 'N/A', theme: 'compliance.debug.text' };
  };

  const handleFieldClick = (mappingKey: string, contractDocumentId: string) => {
    onFieldClick([mappingKey], contractDocumentId);
  };

  // Empty state
  if (fieldsByContractDoc.length === 0) {
    return (
      <Text color="compliance.text.muted" fontSize="sm" fontStyle="italic">
        No extracted fields available for this document
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={3}>
      {fieldsByContractDoc.map(
        (
          { contractDocumentId, documentName, fields, isInteractive },
          docIndex
        ) => (
          <Box key={contractDocumentId}>
            {/* Document Name Header */}
            <HStack justify="space-between" mb={2}>
              <Text
                color="compliance.debug.text"
                fontSize="xs"
                fontWeight="semibold"
                textTransform="uppercase"
              >
                {documentName}
              </Text>
              <Tooltip
                hasArrow
                bg="compliance.tooltip.bg"
                color="compliance.tooltip.text"
                label="Compare Field Values"
                placement="top"
              >
                <IconButton
                  aria-label="Compare field values"
                  color="compliance.debug.text"
                  icon={<Columns3Icon size={14} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => handleCompareClick(contractDocumentId)}
                />
              </Tooltip>
            </HStack>

            {/* Fields List */}
            <VStack align="stretch" spacing={0}>
              {fields.map((field, i) => {
                const { message, theme } = formatValue(
                  field.value,
                  field.exists
                );

                return (
                  <Box key={`${contractDocumentId}-${field.mappingKey}-${i}`}>
                    <HStack
                      _hover={
                        isInteractive
                          ? {
                              bg: 'compliance.item.hover.bg',
                            }
                          : undefined
                      }
                      cursor={isInteractive ? 'pointer' : 'default'}
                      justify="space-between"
                      px={2}
                      py={2}
                      spacing={2}
                      transition="background-color 0.2s"
                      onClick={
                        isInteractive
                          ? () =>
                              handleFieldClick(
                                field.mappingKey,
                                contractDocumentId
                              )
                          : undefined
                      }
                    >
                      {/* Field Label and Mapping Key */}
                      <HStack maxW="70%" spacing={1}>
                        <Text
                          color="compliance.debug.text"
                          fontSize="xs"
                          fontWeight="medium"
                        >
                          {field.mappingKey}
                        </Text>
                        {field.isComputed && (
                          <Text
                            color="compliance.debug.code.text.warning"
                            fontSize="xs"
                            fontStyle="italic"
                          >
                            (computed)
                          </Text>
                        )}
                      </HStack>

                      {/* Field Value */}
                      {isInteractive ? (
                        <Tooltip
                          hasArrow
                          bg="compliance.tooltip.bg"
                          borderColor="compliance.tooltip.border"
                          borderWidth={1}
                          color="compliance.tooltip.text"
                          label={message}
                          placement="left"
                        >
                          <Text
                            color={theme}
                            fontSize="xs"
                            maxW="150px"
                            noOfLines={1}
                            textAlign="right"
                          >
                            {message}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text
                          color={theme}
                          fontSize="xs"
                          maxW="150px"
                          noOfLines={1}
                          textAlign="right"
                        >
                          {message}
                        </Text>
                      )}
                    </HStack>
                  </Box>
                );
              })}
            </VStack>

            {/* Divider between contract documents */}
            {docIndex < fieldsByContractDoc.length - 1 && (
              <Divider mt={3} opacity={0.5} />
            )}
          </Box>
        )
      )}

      <FieldValuesComparisonModal
        contractDocuments={selectedContractDocuments}
        isOpen={isComparisonModalOpen}
        onClose={onComparisonModalClose}
      />
    </VStack>
  );
};
